import Sticky from "react-stickynode"
import { useTranslation } from "next-i18next"
import { serverSideTranslations } from "next-i18next/serverSideTranslations"
import dynamic from "next/dynamic"
import MetaData from "@/common/components/MetaData"
import SectionLine from "@/common/components/_UI/SectionLine"

import ComplyFlowDark from "@/common/assets/image/products/icons/ComplyFlowDark.svg"
import Onboarding from "@/common/assets/image/products/compliance/complyFlow/onboarding.webp"
import DataCollection from "@/common/assets/image/products/compliance/complyFlow/dataCollection.webp"
import Digitalize from "@/common/assets/image/products/compliance/complyFlow/digitalize.webp"
import AutoKYCAML from "@/common/assets/image/products/compliance/complyFlow/autoKYCAML.webp"

const NavbarNew = dynamic(import("@/containers/common/NavbarNew"))

import { DarkBGWrapper, LayoutWrapper } from "@/common/components/LayoutStyle"
import ProductCTA from "@/common/components/ProductCTA"
import ProductViewMore from "@/common/components/ProductViewMore"
import { ProductBanner } from "@/common/components/ProductBanner"
import { ProductNames, SolutionNames } from "@/common/data/Products"
import Footer from "@/containers/common/FooterNew"
import NextImg from "@/common/components/NextImg"

const ComplyFlowPage = () => {
  const { t } = useTranslation(["complyFlow"])
  const { t: commonT } = useTranslation(["common"])

  return (
    <>
      <MetaData
        title={t("meta.title")}
        description={t("meta.description")}
        url={t("meta.url")}
        image="https://storage.googleapis.com/kryptogo-official-website-public/og-logo.webp"
        languageAlternates={[
          {
            hrefLang: "en-US",
            href: "https://www.kryptogo.com/products/comply-flow"
          },
          {
            hrefLang: "zh-TW",
            href: "https://www.kryptogo.com/tw/products/comply-flow"
          },
          {
            hrefLang: "zh-CN",
            href: "https://www.kryptogo.com/cn/products/comply-flow"
          },
          {
            hrefLang: "ja-JP",
            href: "https://www.kryptogo.com/ja/products/comply-flow"
          }
        ]}
      />

      <Sticky top={0} innerZ={1000} activeClass="sticky-active">
        <NavbarNew />
      </Sticky>
      <DarkBGWrapper></DarkBGWrapper>
      <LayoutWrapper>
        <ProductBanner
          icon={ComplyFlowDark.src}
          bannerType={ProductNames.ComplyFlow}
          title={t("Banner.productName")}
          titleh1={t("Banner.title")}
          description={t("Banner.description")}
        />
        <SectionLine />
        <ProductBanner
          short
          paddingTop="0"
          titleh3={t("Desc.1.title")}
          titleh3Type="normal"
          description={t("Desc.1.content")}
          comp2={<NextImg src={Onboarding.src} />}
        />
        <ProductBanner
          short
          paddingTop="0"
          titleh3={t("Desc.2.title")}
          titleh3Type="normal"
          description={t("Desc.2.content")}
          comp2={<NextImg src={DataCollection.src} />}
        />
        <ProductBanner
          short
          paddingTop="0"
          titleh3={t("Banner.2.subtitle")}
          titleh3Type="normal"
          description={t("Banner.2.description")}
          checkList={[
            {
              listColor: "disabled",
              list: [
                { description: t("Banner.2.checkList.0") },
                { description: t("Banner.2.checkList.1") },
                { description: t("Banner.2.checkList.2") }
              ]
            }
          ]}
          comp2={<NextImg src={Digitalize.src} alt="ComplyFlow 2" />}
        />
        <ProductBanner
          short
          paddingTop="0"
          titleh3={t("Banner.3.title")}
          titleh3Type="normal"
          description={t("Banner.3.description")}
          comp2={<NextImg src={AutoKYCAML.src} />}
        />
        <ProductCTA
          title={t("CTA.title")}
          desc={t("CTA.desc")}
          primaryButton={{ text: t("CTA.button") }}
        />
        <ProductViewMore
          commonT={commonT}
          title={commonT("ViewMore.title")}
          tab={[
            {
              name: commonT("ViewMore.products"),
              content: [
                ProductNames.CompliancePro,
                ProductNames.ComplianceLite,
                ProductNames.User360
              ]
            },
            {
              name: commonT("ViewMore.solutions"),
              content: [SolutionNames.Defi]
            }
          ]}
          actionText={commonT("ViewMore.allProducts")}
        ></ProductViewMore>
        <Footer />
      </LayoutWrapper>
    </>
  )
}

export const getStaticProps = async ({ locale }: { locale: string }) => ({
  props: {
    ...(await serverSideTranslations(locale, [
      "common",
      "compliance",
      "contactUs",
      "complyFlow"
    ]))
  }
})

export default ComplyFlowPage
